/**
 * Outil « Gram-Schmidt et projection » — logique pure (prépa).
 *
 * Tout est EXACT : les vecteurs saisis sont entiers, les vecteurs orthogonaux
 * u_k sont à coordonnées rationnelles, et normes, produits scalaires avec les
 * ε_k s'écrivent c·√r (r sans facteur carré). Notations de la carte
 * « Procédé d'orthonormalisation de Gram-Schmidt » : famille (e₁, e₂, e₃),
 * u_k = e_k − Σ ⟨e_k, ε_i⟩ ε_i, ε_k = u_k / ‖u_k‖ ; et de « Projection
 * orthogonale et distance » : p_F(x) = Σ ⟨x, ε_i⟩ ε_i, d(x, F) = ‖x − p_F(x)‖.
 */

import { add, div, frac, isZero, mul, sub, value, type Frac } from '../../ui/exact';

/** vecteur à coordonnées entières (ce qu'on saisit) */
export type IV = readonly [number, number, number];
/** vecteur à coordonnées rationnelles */
export type QV = readonly [Frac, Frac, Frac];

const ZERO = frac(0);
const SUB = ['₀', '₁', '₂', '₃', '₄', '₅', '₆', '₇', '₈', '₉'];
export const idx = (k: number) => SUB[k] ?? String(k);

/* ---------- vecteurs rationnels ---------- */

export const toQ = (v: IV): QV => [frac(v[0]), frac(v[1]), frac(v[2])];
export const qdot = (a: QV, b: QV): Frac =>
  add(add(mul(a[0], b[0]), mul(a[1], b[1])), mul(a[2], b[2]));
export const qadd = (a: QV, b: QV): QV => [add(a[0], b[0]), add(a[1], b[1]), add(a[2], b[2])];
export const qsub = (a: QV, b: QV): QV => [sub(a[0], b[0]), sub(a[1], b[1]), sub(a[2], b[2])];
export const qscale = (k: Frac, a: QV): QV => [mul(k, a[0]), mul(k, a[1]), mul(k, a[2])];
export const qzero = (a: QV) => a.every(isZero);
export const qnum = (a: QV): [number, number, number] => [value(a[0]), value(a[1]), value(a[2])];
export const qeq = (a: QV, b: QV) => qzero(qsub(a, b));
export const QZERO: QV = [ZERO, ZERO, ZERO];

function gcd(x: number, y: number): number {
  x = Math.abs(x);
  y = Math.abs(y);
  while (y) [x, y] = [y, x % y];
  return x;
}

/** direction entière primitive de même sens que a (a ≠ 0) : (1/2 ; −1/2 ; 1) → (1 ; −1 ; 2) */
export function primitive(a: QV): IV {
  const l = a.reduce((m, f) => (m * f.q) / gcd(m, f.q), 1);
  const ints = a.map((f) => (f.p * l) / f.q);
  const g = ints.reduce((m, n) => gcd(m, n), 0) || 1;
  return [ints[0]! / g, ints[1]! / g, ints[2]! / g];
}

/* ---------- racines exactes ---------- */

/** c·√r, r ≥ 1 sans facteur carré (r = 1 : rationnel) */
export interface Surd {
  c: Frac;
  r: number;
}

/** n = s²·r avec r sans facteur carré */
export function splitSquare(n: number): { s: number; r: number } {
  let s = 1;
  let r = n;
  for (let d = 2; d * d <= r; d++) {
    while (r % (d * d) === 0) {
      r /= d * d;
      s *= d;
    }
  }
  return { s, r };
}

/** √f exact, f ≥ 0 : √(p/q) = √(pq)/q */
export function sqrtFrac(f: Frac): Surd {
  if (f.p < 0) throw new Error('racine d’un négatif');
  if (f.p === 0) return { c: ZERO, r: 1 };
  const { s, r } = splitSquare(f.p * f.q);
  return { c: frac(s, f.q), r };
}

/** x / √y (y > 0), écrit c·√r : x√y / y */
export function overSqrt(x: Frac, y: Frac): Surd {
  const s = sqrtFrac(y);
  return { c: mul(div(x, y), s.c), r: s.r };
}

export const surdValue = (s: Surd) => value(s.c) * Math.sqrt(s.r);

export function fracText(f: Frac): string {
  const sign = f.p < 0 ? '−' : '';
  const n = Math.abs(f.p);
  return f.q === 1 ? `${sign}${n}` : `${sign}${n}/${f.q}`;
}

/** « 3√2/2 », « √6/2 », « −√3 », « 5/3 », « 0 » */
export function surdText(s: Surd): string {
  if (s.r === 1 || isZero(s.c)) return fracText(s.c);
  const sign = s.c.p < 0 ? '−' : '';
  const n = Math.abs(s.c.p);
  return `${sign}${n === 1 ? '' : n}√${s.r}${s.c.q === 1 ? '' : `/${s.c.q}`}`;
}

/** « √(3/2) », « √6 », « 3 » : la racine telle qu'on la pose avant de simplifier */
export function rootOf(f: Frac): string {
  const s = sqrtFrac(f);
  if (s.r === 1) return fracText(s.c);
  return f.q === 1 ? `√${f.p}` : `√(${fracText(f)})`;
}

const NB = String.fromCharCode(0xa0);
const NNB = String.fromCharCode(0x202f);
/** « (1 ; −1/2 ; 2) », insécable : un vecteur ne se coupe jamais en fin de ligne */
export const vecText = (a: QV | IV) =>
  `(${a.map((c) => (typeof c === 'number' ? fracText(frac(c)) : fracText(c))).join(`${NNB};${NB}`)})`;

/** facteur de normalisation 1/‖w‖ d'un vecteur entier : « 1/3 », « 1/√6 », « 1/(3√2) » */
export function invNormText(w: IV): string {
  const n = w[0] * w[0] + w[1] * w[1] + w[2] * w[2];
  const { s, r } = splitSquare(n);
  if (r === 1) return s === 1 ? '' : `1/${s}`;
  return s === 1 ? `1/√${r}` : `1/(${s}√${r})`;
}

/** ε = w/‖w‖ écrit « 1/√6 (1 ; −1 ; 2) » (« (0 ; 0 ; 1) » s'il est déjà unitaire) */
export function unitText(w: IV): string {
  const f = invNormText(w);
  return f ? `${f}${NB}${vecText(w)}` : vecText(w);
}

/* ---------- Gram-Schmidt ---------- */

export interface GSStep {
  /** rang k (1, 2, 3) */
  k: number;
  e: QV;
  /** ⟨e_k, ε_i⟩, i < k (exacts) */
  scal: Surd[];
  /** ⟨e_k, ε_i⟩ ε_i = (⟨e_k, u_i⟩ / ‖u_i‖²) u_i, i < k */
  projs: QV[];
  /** somme des projections (projeté de e_k sur Vect(ε₁ … ε_{k−1})) */
  proj: QV;
  u: QV;
  /** u_k ≠ 0 : la famille (e₁ … e_k) est libre */
  free: boolean;
  /** ‖u_k‖² et ‖u_k‖ */
  norm2: Frac;
  norm: Surd;
  /** direction entière primitive de u_k (si libre) */
  w: IV;
}

/** procédé de Gram-Schmidt ; s'arrête au premier vecteur qui rend la famille liée (inclus) */
export function gramSchmidt(fam: readonly IV[]): GSStep[] {
  const steps: GSStep[] = [];
  for (let k = 1; k <= fam.length; k++) {
    const e = toQ(fam[k - 1]!);
    const scal: Surd[] = [];
    const projs: QV[] = [];
    let proj = QZERO;
    for (const s of steps) {
      const d = qdot(e, s.u);
      scal.push(overSqrt(d, s.norm2));
      const p = qscale(div(d, s.norm2), s.u);
      projs.push(p);
      proj = qadd(proj, p);
    }
    const u = qsub(e, proj);
    const free = !qzero(u);
    const norm2 = qdot(u, u);
    steps.push({
      k,
      e,
      scal,
      projs,
      proj,
      u,
      free,
      norm2,
      norm: sqrtFrac(norm2),
      w: free ? primitive(u) : [0, 0, 0],
    });
    if (!free) break;
  }
  return steps;
}

/* ---------- étapes affichées ---------- */

export type Stage = { kind: 'start' } | { kind: 'norm' | 'orth'; k: number };

/** départ, puis ε₁, puis pour k ≥ 2 : orthogonaliser e_k, normaliser u_k ; arrêt si liée */
export function stagesOf(steps: readonly GSStep[], n: number): Stage[] {
  const out: Stage[] = [{ kind: 'start' }];
  for (const s of steps) {
    if (s.k > n) break;
    if (s.k > 1) out.push({ kind: 'orth', k: s.k });
    if (!s.free) {
      // e₁ nul : une étape pour le dire (il n'y a rien à orthogonaliser)
      if (s.k === 1) out.push({ kind: 'norm', k: 1 });
      break;
    }
    out.push({ kind: 'norm', k: s.k });
  }
  return out;
}

/** « 1² + (−1/2)² + 0² » */
const sumSq = (a: QV) =>
  a
    .map((c) => {
      const t = fracText(c);
      return `${c.p < 0 || c.q !== 1 ? `(${t})` : t}²`;
    })
    .join(' + ');

/** lignes d'explication d'une étape (texte brut, notations de la carte) */
export function stageLines(steps: readonly GSStep[], st: Stage, n: number): string[] {
  if (st.kind === 'start') {
    const names = Array.from({ length: n }, (_, i) => `e${idx(i + 1)}`).join(', ');
    return [
      `On part de la famille (${names}).`,
      'On la rend orthonormée un vecteur à la fois : on retire à chaque vecteur ses projections sur les ε déjà construits, puis on le normalise.',
    ];
  }
  const s = steps[st.k - 1]!;
  const k = idx(st.k);
  if (st.kind === 'orth') {
    const lines = s.scal.map(
      (sc, i) =>
        `⟨e${k}, ε${idx(i + 1)}⟩ = ${surdText(sc)}, donc ⟨e${k}, ε${idx(i + 1)}⟩ ε${idx(i + 1)} = ${vecText(s.projs[i]!)}`,
    );
    const minus = s.scal.map((_, i) => `⟨e${k}, ε${idx(i + 1)}⟩ ε${idx(i + 1)}`).join(' − ');
    lines.push(`u${k} = e${k} − ${minus} = ${vecText(s.u)}`);
    if (!s.free) {
      const span = s.scal.map((_, i) => `e${idx(i + 1)}`).join(', ');
      lines.push(
        `u${k} est nul : e${k} est dans Vect(${span}). La famille est liée, le procédé s’arrête.`,
      );
    } else {
      const checks = s.scal.map((_, i) => `⟨u${k}, ε${idx(i + 1)}⟩ = 0`).join(' et ');
      lines.push(`Vérification : ${checks}.`);
    }
    return lines;
  }
  if (!s.free) return [`e${k} est nul : la famille est liée, le procédé s’arrête.`];
  const u = st.k === 1 ? `u${k} = e${k} = ${vecText(s.u)}` : `u${k} = ${vecText(s.u)}`;
  // u = c·w, w entier : ε a la direction de w (on écrit la mise en facteur)
  const i0 = s.w.findIndex((c) => c !== 0);
  const c = div(s.u[i0]!, frac(s.w[i0]!));
  const factored =
    c.p === 1 && c.q === 1
      ? []
      : [`u${k} = ${fracText(c)}${NB}${vecText(s.w)} : ε${k} a la direction de ${vecText(s.w)}`];
  return [
    u,
    ...factored,
    `‖u${k}‖ = √(${sumSq(s.u)}) = ${rootOf(s.norm2)}${rootOf(s.norm2) === surdText(s.norm) ? '' : ` = ${surdText(s.norm)}`}`,
    `ε${k} = u${k} / ‖u${k}‖ = ${unitText(s.w)}`,
  ];
}

/** phrase courte d'une étape (figure, lecteur d'écran) */
export function stageShort(steps: readonly GSStep[], st: Stage): string {
  if (st.kind === 'start') return 'La famille de départ.';
  const s = steps[st.k - 1]!;
  const k = idx(st.k);
  if (st.kind === 'orth')
    return s.free
      ? `u${k} = ${vecText(s.u)}, orthogonal à ${s.scal.map((_, i) => `ε${idx(i + 1)}`).join(' et ')}.`
      : `u${k} est nul : la famille est liée.`;
  return s.free ? `ε${k} = ${unitText(s.w)}.` : `e${k} est nul.`;
}

/* ---------- projection, distance, coordonnées ---------- */

export interface Projection {
  /** base orthogonale de F (u_i libres) et leurs directions entières */
  basis: GSStep[];
  /** indices des générateurs gardés (un générateur nul ou dépendant est écarté) */
  kept: number[];
  /** ⟨x, ε_i⟩ */
  coords: Surd[];
  /** ⟨x, ε_i⟩ ε_i */
  parts: QV[];
  p: QV;
  /** x − p_F(x) */
  r: QV;
  dist2: Frac;
  dist: Surd;
  /** ‖p_F(x)‖² */
  p2: Frac;
}

/** projeté orthogonal de x sur F = Vect(f₁ … f_m) (via Gram-Schmidt ; vecteurs nuls ignorés) */
export function project(x: IV, gens: readonly IV[]): Projection {
  const basis: GSStep[] = [];
  const kept: number[] = [];
  // on orthonormalise en sautant les vecteurs qui n'ajoutent rien
  gens.forEach((g, gi) => {
    const steps = gramSchmidt([...basis.map((b) => b.w), g]);
    const last = steps[steps.length - 1]!;
    if (steps.length === basis.length + 1 && last.free) {
      basis.push({ ...last, k: basis.length + 1 });
      kept.push(gi);
    }
  });
  const X = toQ(x);
  const coords: Surd[] = [];
  const parts: QV[] = [];
  let p = QZERO;
  for (const b of basis) {
    const d = qdot(X, b.u);
    coords.push(overSqrt(d, b.norm2));
    const part = qscale(div(d, b.norm2), b.u);
    parts.push(part);
    p = qadd(p, part);
  }
  const r = qsub(X, p);
  const dist2 = qdot(r, r);
  return { basis, kept, coords, parts, p, r, dist2, dist: sqrtFrac(dist2), p2: qdot(p, p) };
}

/* ---------- préréglages ---------- */

export interface Preset {
  label: string;
  fam: [IV, IV, IV];
  /** taille imposée (« Famille liée » : 3 vecteurs) */
  n?: 3;
}

export const PRESETS: Preset[] = [
  {
    // plan Vect(e₁, e₂) de normale (1 ; −1 ; 1) : bien visible dans la vue de départ
    label: 'Exemple classique',
    fam: [
      [1, 1, 0],
      [0, 1, 1],
      [1, 0, 1],
    ],
  },
  {
    label: 'Vers la base canonique',
    fam: [
      [1, 0, 0],
      [1, 1, 0],
      [1, 1, 1],
    ],
  },
  {
    label: 'Normes entières',
    fam: [
      [1, 2, 2],
      [3, 3, 0],
      [0, 0, 3],
    ],
  },
  {
    label: 'Déjà orthogonale',
    fam: [
      [1, 1, 1],
      [1, -1, 0],
      [1, 1, -2],
    ],
  },
  {
    label: 'Famille liée',
    n: 3,
    fam: [
      [1, 2, 0],
      [2, 1, 0],
      [1, 1, 0],
    ],
  },
];

export const COORD_MIN = -4;
export const COORD_MAX = 4;

/* ---------- défi ---------- */

export type QuizKind = 'normalize' | 'orth' | 'proj' | 'coord' | 'dist';
export const QUIZ_KINDS: QuizKind[] = ['normalize', 'orth', 'proj', 'coord', 'dist'];

export interface QuizOption {
  text: string;
  correct: boolean;
  hint?: string;
}

export interface Quiz {
  kind: QuizKind;
  /** énoncé (les données sont dedans) */
  prompt: string;
  options: QuizOption[];
  /** explication de la bonne réponse */
  explain: string;
}

type Rng = () => number;
const int = (lo: number, hi: number, rng: Rng) => lo + Math.floor(rng() * (hi - lo + 1));
const randVec = (rng: Rng, m = 3): IV => [int(-m, m, rng), int(-m, m, rng), int(-m, m, rng)];
const n2 = (v: IV) => v[0] * v[0] + v[1] * v[1] + v[2] * v[2];
const idot = (a: IV, b: IV) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const icross = (a: IV, b: IV): IV => [
  a[1] * b[2] - a[2] * b[1],
  a[2] * b[0] - a[0] * b[2],
  a[0] * b[1] - a[1] * b[0],
];
const isPrimitive = (v: IV) => gcd(gcd(v[0], v[1]), v[2]) === 1;
const collinear = (a: IV, b: IV) => icross(a, b).every((c) => c === 0);

function shuffle<T>(a: T[], rng: Rng): T[] {
  const r = [...a];
  for (let i = r.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [r[i], r[j]] = [r[j]!, r[i]!];
  }
  return r;
}

/** garde le premier de chaque valeur (clé) ; null si moins de 4 réponses distinctes */
function finish(
  kind: QuizKind,
  prompt: string,
  cands: (QuizOption & { key: string })[],
  explain: string,
  rng: Rng,
): Quiz | null {
  const seen = new Set<string>();
  const kept: QuizOption[] = [];
  for (const c of cands) {
    if (seen.has(c.key)) continue;
    seen.add(c.key);
    kept.push({ text: c.text, correct: c.correct, ...(c.hint ? { hint: c.hint } : {}) });
  }
  if (kept.length < 4) return null;
  return { kind, prompt, options: shuffle(kept.slice(0, 4), rng), explain };
}

/** « 1/6 e₁ », « e₁ », « −e₁ », « (−1/6) e₁ » */
function scaled(c: Frac, name: string): string {
  if (c.p === 0) return '0';
  if (c.q === 1 && Math.abs(c.p) === 1) return c.p > 0 ? name : `−${name}`;
  return c.p < 0 ? `(${fracText(c)})${NB}${name}` : `${fracText(c)}${NB}${name}`;
}
/** « e₂ − 1/6 e₁ », « e₂ + e₁ » : a + c·b, signe porté par l'opération */
function combo(a: string, c: Frac, b: string): string {
  if (c.p === 0) return a;
  const abs = frac(Math.abs(c.p), c.q);
  return `${a} ${c.p < 0 ? '−' : '+'} ${scaled(abs, b)}`;
}

const vkey = (v: QV) => v.map((f) => `${f.p}/${f.q}`).join(',');
const skey = (s: Surd) => surdValue(s).toFixed(9);
const qv = (v: IV) => toQ(v);

/** e primitif, au moins deux coordonnées non nulles et une de valeur absolue ≥ 2 */
function niceVec(rng: Rng): IV {
  for (;;) {
    const v = randVec(rng);
    const nz = v.filter((c) => c !== 0).length;
    if (nz >= 2 && v.some((c) => Math.abs(c) >= 2) && isPrimitive(v)) return v;
  }
}

function quizNormalize(rng: Rng): Quiz | null {
  const e = niceVec(rng);
  const N = n2(e);
  const S = Math.abs(e[0]) + Math.abs(e[1]) + Math.abs(e[2]);
  const ok = unitText(e);
  const { s, r } = splitSquare(N);
  const root = r === 1 ? String(s) : s === 1 ? `√${r}` : `${s}√${r}`;
  const cands = [
    { text: ok, correct: true, key: (1 / Math.sqrt(N)).toFixed(9) },
    {
      text: `1/${N} ${vecText(e)}`,
      correct: false,
      key: (1 / N).toFixed(9),
      hint: 'Tu as divisé par ‖e₁‖² au lieu de ‖e₁‖.',
    },
    {
      text: `1/${S} ${vecText(e)}`,
      correct: false,
      key: (1 / S).toFixed(9),
      hint: 'La norme n’est pas la somme des valeurs absolues des coordonnées : ‖e₁‖ = √(x² + y² + z²).',
    },
    {
      text: `${root} ${vecText(e)}`,
      correct: false,
      key: Math.sqrt(N).toFixed(9),
      hint: 'Il faut diviser par la norme, pas multiplier.',
    },
  ];
  return finish(
    'normalize',
    `Normalise e₁ = ${vecText(e)} : que vaut ε₁ = e₁/‖e₁‖ ?`,
    cands,
    `‖e₁‖ = √(${sumSq(qv(e))}) = ${rootOf(frac(N))}, donc ε₁ = ${ok}.`,
    rng,
  );
}

function quizOrth(rng: Rng): Quiz | null {
  const e1 = niceVec(rng);
  const e2 = randVec(rng);
  const d = idot(e1, e2);
  const N = n2(e1);
  if (d === 0 || collinear(e1, e2)) return null;
  const E1 = qv(e1);
  const E2 = qv(e2);
  const u = qsub(E2, qscale(frac(d, N), E1));
  const a = qsub(E2, qscale(frac(d), E1));
  const b = qadd(E2, qscale(frac(d, N), E1));
  const c = qsub(E1, qscale(frac(d, n2(e2)), E2));
  return finish(
    'orth',
    `e₁ = ${vecText(e1)}, e₂ = ${vecText(e2)} et ε₁ = e₁/‖e₁‖. Que vaut u₂ = e₂ − ⟨e₂, ε₁⟩ ε₁ ?`,
    [
      { text: vecText(u), correct: true, key: vkey(u) },
      {
        text: vecText(a),
        correct: false,
        key: vkey(a),
        hint: 'e₁ n’est pas unitaire : ⟨e₂, ε₁⟩ ε₁ = (⟨e₂, e₁⟩ / ‖e₁‖²) e₁.',
      },
      {
        text: vecText(b),
        correct: false,
        key: vkey(b),
        hint: 'On RETIRE la projection : signe −.',
      },
      {
        text: vecText(c),
        correct: false,
        key: vkey(c),
        hint: 'On garde e₁ et on corrige e₂, pas l’inverse.',
      },
    ],
    `⟨e₂, e₁⟩ = ${fracText(frac(d))} et ‖e₁‖² = ${N}, donc u₂ = ${combo('e₂', frac(-d, N), 'e₁')} = ${vecText(u)} ; on vérifie ⟨u₂, e₁⟩ = 0.`,
    rng,
  );
}

function quizProj(rng: Rng): Quiz | null {
  const e = niceVec(rng);
  const x = randVec(rng);
  const d = idot(x, e);
  const N = n2(e);
  const X2 = n2(x);
  if (d === 0 || collinear(x, e) || X2 === N) return null;
  const E = qv(e);
  const X = qv(x);
  const p = qscale(frac(d, N), E);
  const r = qsub(X, p);
  const raw = qscale(frac(d), E);
  const wrong = qscale(frac(d, X2), E);
  return finish(
    'proj',
    `D = Vect(e₁) avec e₁ = ${vecText(e)}. Quel est le projeté orthogonal de x = ${vecText(x)} sur D ?`,
    [
      { text: vecText(p), correct: true, key: vkey(p) },
      {
        text: vecText(r),
        correct: false,
        key: vkey(r),
        hint: 'Ça, c’est x − p_D(x) : la composante orthogonale à D.',
      },
      {
        text: vecText(raw),
        correct: false,
        key: vkey(raw),
        hint: 'e₁ n’est pas unitaire : il faut diviser par ‖e₁‖².',
      },
      {
        text: vecText(wrong),
        correct: false,
        key: vkey(wrong),
        hint: 'On divise par ‖e₁‖², pas par ‖x‖².',
      },
    ],
    `⟨x, e₁⟩ = ${fracText(frac(d))} et ‖e₁‖² = ${N}, donc p_D(x) = (⟨x, e₁⟩/‖e₁‖²) e₁ = ${scaled(frac(d, N), 'e₁')} = ${vecText(p)}.`,
    rng,
  );
}

/** bases orthonormées « propres » : ε_i = w_i / ‖w_i‖ */
export const ON_BASES: [IV, IV, IV][] = [
  [
    [1, 1, 0],
    [1, -1, 0],
    [0, 0, 1],
  ],
  [
    [1, 2, 2],
    [2, 1, -2],
    [2, -2, 1],
  ],
  [
    [1, 1, 1],
    [1, -1, 0],
    [1, 1, -2],
  ],
  [
    [0, 1, 1],
    [0, 1, -1],
    [1, 0, 0],
  ],
];

function quizCoord(rng: Rng): Quiz | null {
  const base = ON_BASES[int(0, ON_BASES.length - 1, rng)]!;
  const j = int(0, 2, rng);
  const w = base[j]!;
  if (n2(w) === 1) return null;
  const k = (j + 1 + int(0, 1, rng)) % 3;
  const x = randVec(rng);
  const d = idot(x, w);
  if (d === 0) return null;
  const ok = overSqrt(frac(d), frac(n2(w)));
  const other = overSqrt(frac(idot(x, base[k]!)), frac(n2(base[k]!)));
  const J = idx(j + 1);
  const basis = base.map((b, i) => `ε${idx(i + 1)} = ${unitText(b)}`).join(', ');
  return finish(
    'coord',
    `(ε₁, ε₂, ε₃) est orthonormée : ${basis}. Quelle est la coordonnée de x = ${vecText(x)} sur ε${J} ?`,
    [
      { text: surdText(ok), correct: true, key: skey(ok) },
      {
        text: String(d).replace('-', '−'),
        correct: false,
        key: skey({ c: frac(d), r: 1 }),
        hint: `Tu as oublié le facteur ${invNormText(w)} de ε${J}.`,
      },
      {
        text: fracText(frac(d, n2(w))),
        correct: false,
        key: skey({ c: frac(d, n2(w)), r: 1 }),
        hint: `Le facteur est ${invNormText(w)}, pas son carré.`,
      },
      {
        text: surdText(other),
        correct: false,
        key: skey(other),
        hint: `C’est la coordonnée sur ε${idx(k + 1)}.`,
      },
    ],
    `La base est orthonormée : la coordonnée sur ε${J} est ⟨x, ε${J}⟩ = ${invNormText(w)} × ⟨x, ${vecText(w)}⟩ = ${invNormText(w)} × ${d < 0 ? `(${fracText(frac(d))})` : d} = ${surdText(ok)}.`,
    rng,
  );
}

function quizDist(rng: Rng): Quiz | null {
  const e1 = niceVec(rng);
  const r0 = randVec(rng);
  if (collinear(e1, r0) || r0.every((c) => c === 0)) return null;
  const c0 = icross(e1, r0);
  const g = gcd(gcd(c0[0], c0[1]), c0[2]) || 1;
  const e2: IV = [c0[0] / g, c0[1] / g, c0[2] / g];
  if (e2.some((c) => Math.abs(c) > 4)) return null;
  const x = randVec(rng);
  const P = project(x, [e1, e2]);
  if (isZero(P.dist2) || value(P.dist2) === 1) return null;
  const xn = sqrtFrac(frac(n2(x)));
  // erreur classique : ne retirer que la projection sur e₁ (distance à la droite Vect(e₁))
  const line = project(x, [e1]).dist;
  return finish(
    'dist',
    `P = Vect(e₁, e₂) avec e₁ = ${vecText(e1)} et e₂ = ${vecText(e2)} (orthogonaux). Que vaut la distance de x = ${vecText(x)} à P ?`,
    [
      { text: surdText(P.dist), correct: true, key: skey(P.dist) },
      {
        text: fracText(P.dist2),
        correct: false,
        key: skey({ c: P.dist2, r: 1 }),
        hint: 'C’est d², il reste la racine.',
      },
      {
        text: surdText(line),
        correct: false,
        key: skey(line),
        hint: 'C’est la distance à la droite Vect(e₁) : il faut retirer aussi la projection sur e₂.',
      },
      {
        text: surdText(xn),
        correct: false,
        key: skey(xn),
        hint: 'C’est la distance de x à 0, pas au plan.',
      },
    ],
    `e₁ ⟂ e₂ : p_P(x) = (⟨x, e₁⟩/‖e₁‖²) e₁ + (⟨x, e₂⟩/‖e₂‖²) e₂ = ${combo(scaled(frac(idot(x, e1), n2(e1)), 'e₁'), frac(idot(x, e2), n2(e2)), 'e₂')} = ${vecText(P.p)}, x − p_P(x) = ${vecText(P.r)}, donc d(x, P) = ${rootOf(P.dist2)}${rootOf(P.dist2) === surdText(P.dist) ? '' : ` = ${surdText(P.dist)}`}.`,
    rng,
  );
}

const MAKERS: Record<QuizKind, (rng: Rng) => Quiz | null> = {
  normalize: quizNormalize,
  orth: quizOrth,
  proj: quizProj,
  coord: quizCoord,
  dist: quizDist,
};

export function makeQuiz(kind: QuizKind, rng: Rng): Quiz {
  for (let i = 0; i < 400; i++) {
    const q = MAKERS[kind](rng);
    if (q) return q;
  }
  throw new Error(`défi ${kind} introuvable`);
}
